'use client';

// ═══════════════════════════════════════════════════════════════════════
// PendingReviewSideSlide — first-contact bottom-right alert.
//
// Shows when:
//   • the queue is non-empty,
//   • the panel is in 'hidden' state (so it acts as a re-entry prompt
//     for new reviews after the user closed the panel),
//   • the user hasn't dismissed the current top review.
//
// Compact preview surfaces up to 3 candidates from the current review
// (with "+N more" if longer). Tap Expand → opens the side-pinned
// PendingReviewPanel. Dismiss hides the slide; review stays pending.
// ═══════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { ChevronUp, Clock, X, Zap } from 'lucide-react';
import type { PendingReview } from '@/lib/api-client';

interface PendingReviewSideSlideProps {
  review: PendingReview;
  /** Number of other pending reviews beyond this one. Surfaced as a
   *  "+N more" badge so the user knows the queue is deep. */
  moreCount: number;
  onExpand: () => void;
  onDismiss: () => void;
  /** Server-side snooze for N minutes — pauses ALL trades for the
   *  portfolio for that long and terminates the current review row.
   *  Optional: when omitted the snooze chips don't render (callers
   *  that don't want server-side snooze can just skip the prop). */
  onSnooze?: (minutes: number) => Promise<void>;
}

const PREVIEW_LIMIT = 3;

export default function PendingReviewSideSlide({
  review, moreCount, onExpand, onDismiss, onSnooze,
}: PendingReviewSideSlideProps) {
  const [snoozing, setSnoozing] = useState<number | null>(null);
  const handleSnooze = async (minutes: number) => {
    if (!onSnooze || snoozing != null) return;
    setSnoozing(minutes);
    try { await onSnooze(minutes); } finally { setSnoozing(null); }
  };
  if (review.candidates.length === 0) return null;
  const ranked = review.candidates.slice().sort((a, b) => a.score - b.score);
  const preview = ranked.slice(0, PREVIEW_LIMIT);
  const overflow = ranked.length - preview.length;
  const isBuy = review.action === 'buy';
  const chipColor = isBuy
    ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
    : 'bg-rose-500/15 border-rose-500/40 text-rose-300';

  return (
    <div
      role="dialog"
      aria-label="Pending auto-trade review preview"
      className="fixed z-[100] right-4 bottom-4 sm:max-w-sm w-[calc(100%-2rem)] sm:w-96"
    >
      <div className="bg-card-bg border border-default rounded-xl shadow-2xl p-3 sm:p-4 animate-in slide-in-from-bottom-2 fade-in duration-200">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <Zap size={14} className="text-fuchsia-400 shrink-0" />
            <div className="text-[11px] uppercase tracking-wide text-fg-subtle truncate">
              {review.portfolioName ?? 'Auto-trade review'}
            </div>
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border shrink-0 ${chipColor}`}>
              {isBuy ? 'Buy' : 'Sell'}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-300 font-mono shrink-0">
              {ranked.length}
            </span>
            {moreCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-card-bg border border-default text-fg-subtle shrink-0">
                +{moreCount}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss until next alert"
            className="text-fg-subtle hover:text-white p-1 -mt-1 -mr-1 rounded transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        <div className="space-y-1 mb-3">
          {preview.map((c) => (
            <div key={c.ticker} className="flex items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-white font-bold font-mono">{c.ticker}</span>
                <span className="text-fg-subtle text-[10px] truncate">{c.qualificationReason}</span>
              </div>
              <div className="text-right shrink-0 flex items-center gap-2">
                <span className="text-white font-mono">${c.price.toFixed(2)}</span>
                {isBuy && (
                  <span className={`font-mono text-[11px] ${c.changePct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {c.changePct >= 0 ? '+' : ''}{c.changePct.toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
          ))}
          {overflow > 0 && (
            <div className="text-[10px] text-fg-subtle pl-0.5">+{overflow} more candidate{overflow === 1 ? '' : 's'}</div>
          )}
        </div>

        {/* Action row. Expand stays the primary CTA on the left;
            snooze chips + an icon-only Dismiss share the remainder.
            Snoozes call the server (terminate review + pause portfolio
            trades for N min). Dismiss is the original client-only
            "hide until next tick" — keeps the review pending so the
            panel can still show it. */}
        <div className="flex items-center gap-2">
          {/* Expand + snooze chips share `flex-1` so the row reads as
              three equal-width pills. Previously Expand alone had
              flex-1 and dominated the row (~3× the chip width); now
              it's roughly the same size as 15m / 30m, which is more
              honest about action priority — snooze and expand are
              both equally valid responses to "what should I do with
              this review". */}
          <button
            type="button"
            onClick={onExpand}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-200 hover:bg-sky-500/25 transition-colors min-h-[36px]"
          >
            <ChevronUp size={14} />
            Expand
          </button>
          {onSnooze && (
            <>
              <button
                type="button"
                onClick={() => handleSnooze(15)}
                disabled={snoozing != null}
                title="Snooze portfolio trades for 15 minutes"
                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-2 text-xs rounded-md bg-card-bg border border-default text-fg-subtle hover:text-white transition-colors disabled:opacity-40 min-h-[36px]"
              >
                <Clock size={11} />{snoozing === 15 ? '…' : '15m'}
              </button>
              <button
                type="button"
                onClick={() => handleSnooze(30)}
                disabled={snoozing != null}
                title="Snooze portfolio trades for 30 minutes"
                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-2 text-xs rounded-md bg-card-bg border border-default text-fg-subtle hover:text-white transition-colors disabled:opacity-40 min-h-[36px]"
              >
                <Clock size={11} />{snoozing === 30 ? '…' : '30m'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onDismiss}
            disabled={snoozing != null}
            title="Dismiss until the next alert (review stays pending in the panel)"
            aria-label="Dismiss until next alert"
            className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-card-bg border border-default text-fg-subtle hover:text-white transition-colors disabled:opacity-40"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
